import Swal from "sweetalert2";

/**
 * Remove any trailing slashes from a path
 * @param {string} path segment of the URL
 * @returns string path without trailing slashes
 */
export const removeTrailingSlash = (path) => {
    return path.replace(/\/+$/, ''); // Remove trailing slashes
};
  
/**
 * Remove any leading slashes from a path
 * @param {string} path segment of the URL
 * @returns string path without leading slashes
 */
export const removeLeadingSlash = (path) => {
    const normalizedPath = String(path || "");
    return normalizedPath.startsWith('/') ? normalizedPath.substring(1) : normalizedPath;
};
  
/**
 * Remove any leading + trailing slashes from a path
 * @param {string} path segment of the URL
 * @returns string path without leading or trailing slashes
 */
export const removeSlashes = (path) => {
    if (!path) return "";  // Exit if path is undefined, null, or empty string
    if (typeof path !== 'string') return path; // Return path if it's not a string
    return removeTrailingSlash(removeLeadingSlash(path)); // Remove leading + trailing slashes
};

/**
 * Get the value of an environment variable
 * taking into account the environment (Kubernetes, Vite, or local Node.js)
 * @param {string} key name of the environment variable
 * @returns string value of the environment variable or undefined if not found 
 */
export const getEnvVariable = (key) => {

    if (typeof import.meta !== 'undefined' && import.meta.env) {
      // Use import.meta.env for Kubernetes or Vite-based environments
      return import.meta.env[key];
    } else if (process.env) {
      // Use process.env for local Node.js environments
      return process.env[key];
    } else {
      console.error(`Environment variable ${key} not found`);
      return undefined;
    }
};
export const showNetworkError = (headerText, subHeaderText) => {
    Swal.fire({
        html: `
        <div class="avatar avatar-icon avatar-soft-danger mb-3">
            <span class="initial-wrap"><i class="ri-close-circle-fill"></i></span>
        </div>
        <div>
            <h4 class="text-danger">${headerText}</h4>
            <p class="mt-2">${subHeaderText}</p>
        </div>`,
        customClass: {
        container: "swal2-has-footer",
        confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
        footer: ' ',
        // footer: '<a href="#">Why do I have this issue?</a>',
    });
};

export const STALE_TOKEN_MESSAGE =
    'This operation requires a fresh sign-in. Please sign out and sign in again, then retry.';

export const showStaleTokenAlert = () => {
    Swal.fire({
        html: `
        <div class="avatar avatar-icon avatar-soft-danger mb-3">
            <span class="initial-wrap"><i class="ri-close-circle-fill"></i></span>
        </div>
        <div>
            <h4 class="text-danger">Session token is not fresh</h4>
            <p class="mt-2">${STALE_TOKEN_MESSAGE}</p>
        </div>`,
        customClass: {
            container: "swal2-has-footer",
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-secondary ms-2",
        },
        buttonsStyling: false,
        confirmButtonText: 'Go to sign out',
        showCancelButton: true,
        cancelButtonText: 'Close',
    }).then((result) => {
        if (result.isConfirmed) {
            window.location.href = '/signout';
        }
    });
};

export const isStaleTokenMessage = (message) => {
    const text = String(message || '').toLowerCase();
    return text.includes('token is not fresh') || text.includes('not fresh');
};

/**
 * User-facing detail for MSAL / Entra login failures (includes AADSTS code when present).
 * @param {unknown} error
 * @returns {string}
 */
export const formatMsalAuthError = (error) => {
    if (!error) {
        return 'Please sign out, clear your browser session for this site, and sign in again.';
    }
    const err = /** @type {{ errorCode?: string, errorMessage?: string, message?: string, subError?: string }} */ (
        error
    );
    const parts = [];
    if (err.errorCode) parts.push(err.errorCode);
    if (err.subError) parts.push(err.subError);
    const message = err.errorMessage || err.message;
    if (message) parts.push(message);
    if (parts.length === 0) {
        return 'Please sign out, clear your browser session for this site, and sign in again.';
    }
    const detail = parts.join(': ');
    if (detail.includes('AADSTS500208')) {
        return `${detail}. BioMADEEntra is an Entra External ID (CIAM) tenant — use VITE_ENTRA_AUTH_MODE=ciam and VITE_ENTRA_TENANT_SUBDOMAIN=BioMADEEntra (not login.microsoftonline.com workforce). Bad Dog TokenValidator must accept the ciamlogin.com token issuer. Verify ${msalRedirectHint()} is registered on the Entra app.`;
    }
    return `${detail}. If this persists, verify VITE_ENTRA_* matches Bad Dog and that ${msalRedirectHint()} is registered on the Entra app.`;
};

function msalRedirectHint() {
    try {
        const env =
            typeof import.meta !== 'undefined' && import.meta.env
                ? import.meta.env
                : typeof process !== 'undefined' && process.env
                  ? process.env
                  : {};
        return env.VITE_AUTH_REDIRECT_URI || 'http://localhost:32173/redirect';
    } catch {
        return 'your redirect URI';
    }
}
